import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { IOption, SelectComponent } from '@cmps/ng-select';
import { CmpsDialog } from '@cmps/ng-uikit';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { SecurityScope } from '../../../../auth/auth.model';
import { AuthService } from '../../../../auth/auth.service';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { SearchUserContainerComponent } from '../../../search-user.container.component';
import { User } from '../../../user.model';
import { PopulationPropsMap } from '../../const/population-props-map.const';
import { Population } from '../../enum/population.enum';
import { addToSelection } from '../../function/add-to-selection.function';
import { load } from '../../function/load.function';
import {
    setOptions,
    setOptionsWhenChanged,
} from '../../function/set-options.function';
import { setSelectionLabels } from '../../function/set-selection-labels.function';
import { setSelection } from '../../function/set-selection.function';
import { MainState } from '../../model/main-state.model';
import { PopulationOptions } from '../../model/population-options.model';
import { SelectedItem } from '../../model/selected-item.model';
import {
    createMainState,
    deleteMainState,
} from '../../population-select.state';

@Component({
    selector: 'cmps-population-select',
    templateUrl: './main.component.html',
})
@TakeUntilDestroy()
export class MainComponent
    implements OnChanges, OnInit, OnDestroy, PopulationOptions
{
    @Input() canSelectUser: boolean;
    @Input() canSelectTeam: boolean;
    @Input() canSelectLob: boolean;
    @Input() canSelectRegion: boolean;
    @Input() canSelectArea: boolean;
    @Input() canSelectSite: boolean;
    @Input() canSelectSuperGroup: boolean;
    @Input() canSelectPeerGroup: boolean;
    @Input() canSelectRole: boolean;
    @Input() canSelectQueue: boolean;
    @Input() canSelectPopulationGroup: boolean;

    @Input() selectableLobIds: number[];
    @Input() selectableRegionIds: number[];
    @Input() selectableAreaIds: number[];
    @Input() selectableSiteIds: number[];
    @Input() selectableSuperGroupIds: number[];
    @Input() selectablePeerGroupIds: number[];
    @Input() selectableRoleIds: number[];
    @Input() selectableQueueIds: number[];
    @Input() selectablePopulationGroupIds: number[];

    @Input() canSelectAllLobs: boolean;
    @Input() canSelectAllRegions: boolean;
    @Input() canSelectAllAreas: boolean;
    @Input() canSelectAllSites: boolean;
    @Input() canSelectAllSuperGroups: boolean;
    @Input() canSelectAllPeerGroups: boolean;
    @Input() canSelectAllRoles: boolean;
    @Input() canSelectAllQueues: boolean;
    @Input() canSelectAllPopulationGroups: boolean;

    @Input() disabled: boolean;
    @Input() readonly: boolean;
    @Input() singlePopulationSelect: boolean;

    @Input() set selectedPopulation(value: Population) {
        this._state.selectedPopulation = value;
    }

    get selectedPopulation(): Population {
        return this._state.selectedPopulation;
    }

    @Input() set selection(value: SelectedItem[] | null) {
        this._setSelection(value);
    }

    get selection(): SelectedItem[] {
        return this._state.selectedAsArray;
    }

    @Output() selectionChange: EventEmitter<SelectedItem[]>;
    @Output() selectionLabelChange: EventEmitter<SelectedItem[]>;

    /** @private */
    _Population = Population;

    /** @private */
    _PopulationPropsMap = PopulationPropsMap;

    /** @private */
    _state: MainState;

    private readonly _selectionChangeSubj: Subject<void>;

    constructor(
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
    ) {
        this.selectionChange = new EventEmitter();
        this.selectionLabelChange = new EventEmitter();
        this._selectionChangeSubj = new Subject();
        this._state = createMainState(this);
    }

    ngOnChanges(changes: SimpleChanges): void {
        setOptionsWhenChanged(changes, this);
    }

    ngOnInit(): void {
        load()
            .pipe(
                tap(() => {
                    setOptions(this);
                    this._setSelectionLabels();
                    this._state.loaded = true;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.selectionChange.complete();
        this.selectionLabelChange.complete();
        this._selectionChangeSubj.complete();
        deleteMainState(this);
    }

    /** @private */
    _onUserClick(population: Population): void {
        const ref = this._dialog.open<SearchUserContainerComponent, any, User>(
            SearchUserContainerComponent,
            {
                data: {
                    userId: this._authService.user.id,
                    scope: SecurityScope.COACHING /* TODO revise */,
                },
            },
        );
        ref.result
            .pipe(
                tap((user) => {
                    if (user == null) {
                        return;
                    }

                    addToSelection(
                        this,
                        population,
                        user.id,
                        `${user.firstName} ${user.lastName}`,
                        this.singlePopulationSelect,
                    );

                    this.selectionChange.emit(this._state.selectedAsArray);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onChange(
        population: Population,
        selected: IOption,
        ngSelect: SelectComponent,
    ): void {
        if (!this.singlePopulationSelect) {
            ngSelect.select(null);
        }

        addToSelection(
            this,
            population,
            selected.value,
            selected.label,
            this.singlePopulationSelect,
        );

        this.selectionChange.emit(this._state.selectedAsArray);
    }

    private _setSelection(value: SelectedItem[] | null): void {
        this._selectionChangeSubj.next();

        setSelection(this, value, this.singlePopulationSelect);

        if (this._state.loaded === true) {
            this._setSelectionLabels();
        }
    }

    private _setSelectionLabels(): void {
        setSelectionLabels(this)
            .pipe(
                tap(() =>
                    this.selectionLabelChange.emit(this._state.selectedAsArray),
                ),
                takeUntil(this._selectionChangeSubj),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
